import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { useCreateNotification } from '../../api/aws/useCreateNotification'
import { useFetchNotifications } from '../../api/aws/useFetchNotifications'
import { useActiveUser } from '../../api/slack/useActiveUser'
import type {
  CreateNotificationPayload,
  Notification,
  NotificationContextValue,
  NotificationProviderProps,
  SlackUserLite,
} from './types'

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined)

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState<number>(0)

  const { activeUser, isActiveUserLoading } = useActiveUser()

  const { notifications: fetchedNotifications, isNotificationsLoading, isNotificationsError } = useFetchNotifications(
    true,
    activeUser?.user?.id
  )

  const { mutate: createNotification } = useCreateNotification()

  const updateUnreadCount = useCallback(
    (data: Notification[] = notifications) => {
      const unread = data.filter(n => !n.isRead).length
      setUnreadCount(unread)
    },
    [notifications]
  )

  useEffect(() => {
    if (fetchedNotifications && !isNotificationsLoading && !isActiveUserLoading) {
      const sortedNotifications = [...fetchedNotifications].sort((a: Notification, b: Notification) => {
        if (!a.isRead && b.isRead) return -1
        if (a.isRead && !b.isRead) return 1
        return new Date(b.createdTimestamp).getTime() - new Date(a.createdTimestamp).getTime()
      })

      setNotifications(sortedNotifications.filter(n => n.userId === activeUser?.user?.id))
    }
  }, [fetchedNotifications, isNotificationsLoading, isActiveUserLoading, activeUser?.user?.id])

  useEffect(() => {
    updateUnreadCount(notifications)
  }, [notifications, updateUnreadCount])

  const markAsRead = useCallback((notifyUuids: string | string[]) => {
    const uuidList = Array.isArray(notifyUuids) ? notifyUuids : [notifyUuids]

    setNotifications(prev => prev.map(n => (uuidList.includes(n.notify_uuid) ? { ...n, isRead: true } : n)))

    // If you want the count to be immediately correct without relying on state timing:
    setUnreadCount(prev => Math.max(0, prev - uuidList.length))
    // Or keep the existing approach:
    // updateUnreadCount()
  }, [])

  const addNotification = useCallback(
    (payload: CreateNotificationPayload[] | undefined | null) => {
      if (!payload?.length) return

      payload.forEach(notification => {
        createNotification(notification, {
          onSuccess: (data: Notification) => {
            setNotifications(prev => [...prev, data])
            updateUnreadCount()
          },
          onError: (error: unknown) => {
            console.error('Error creating notification:', error)
          },
        })
      })
    },
    [createNotification, updateUnreadCount]
  )

  const createMessagePayload = useCallback(
    (
      userIds: string[],
      type: string,
      severity: string,
      title: string,
      message: string,
      board?: string,
      boardItemName?: string,
      boardId?: string,
      imageUrl?: string,
      url?: string
    ): CreateNotificationPayload[] | undefined => {
      if (!userIds?.length) return

      return userIds.map(id => ({
        userId: id,
        type,
        severity,
        title,
        message,
        board: board || '',
        boardItemName,
        boardId,
        imageUrl,
        url: url || '',
      }))
    },
    []
  )

  const createMentionPayload = useCallback(
    (
      userData: SlackUserLite[],
      type: string,
      severity: string,
      title: string,
      message: string,
      board?: string,
      boardItemName?: string,
      boardId?: string,
      _imageUrl?: string,
      url?: string
    ): CreateNotificationPayload[] | undefined => {
      if (!userData?.length) return

      return userData.map(user => ({
        userId: user.id,
        type,
        severity,
        title,
        message,
        board: board || '',
        boardItemName,
        boardId,
        imageUrl: user.image_512,
        url: url || '',
      }))
    },
    []
  )

  const createTaskMentionPayload = useCallback(
    (
      userData: SlackUserLite[],
      type: string,
      severity: string,
      title: string,
      message: string,
      board?: string,
      boardItemName?: string,
      taskPK?: string,
      taskSK?: string,
      _imageUrl?: string,
      url?: string
    ): CreateNotificationPayload[] | undefined => {
      if (!userData?.length) return

      return userData.map(user => ({
        userId: user.id,
        type,
        severity,
        title,
        message,
        board: board || '',
        boardItemName,
        taskPK,
        taskSK,
        imageUrl: user.image_512,
        url: url || '',
      }))
    },
    []
  )

  const value: NotificationContextValue = {
    notifications,
    unreadCount,
    markAsRead,
    addNotification,
    isNotificationsLoading,
    isNotificationsError,
    createMessagePayload,
    createMentionPayload,
    createTaskMentionPayload,
  }

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>
}

export const useNotify = (): NotificationContextValue => {
  const context = useContext(NotificationContext)
  if (!context) throw new Error('useNotify must be used within a NotificationProvider')
  return context
}
